import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Decode JWT payload without verification (for reading expiry)
const decodeTokenPayload = (token: string): { exp?: number } | null => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

// Proactively refresh tokens before they expire (when <2 min remaining)
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleProactiveRefresh = () => {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);

  const { accessToken, refreshToken } = useAuthStore.getState();
  if (!accessToken || !refreshToken) return;

  const payload = decodeTokenPayload(accessToken);
  if (!payload?.exp) return;

  const nowSec = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = payload.exp - nowSec;
  // Refresh 2 minutes before expiry, minimum 10 seconds from now
  const refreshIn = Math.max((timeUntilExpiry - 120) * 1000, 10000);

  proactiveRefreshTimer = setTimeout(async () => {
    try {
      const { refreshToken: currentRefresh, setTokens } = useAuthStore.getState();
      if (!currentRefresh) return;

      const response = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken: currentRefresh });
      const { accessToken: newAccess, refreshToken: newRefresh } = response.data;
      setTokens(newAccess, newRefresh);
      // Schedule the next proactive refresh
      scheduleProactiveRefresh();
    } catch {
      // Retry once after 30 seconds — if that also fails, the 401 interceptor handles it
      setTimeout(() => {
        scheduleProactiveRefresh();
      }, 30000);
    }
  }, refreshIn);
};

export const cancelProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

// Request interceptor: attach access token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // Attach active profile ID so the backend knows which profile is acting
    const storedProfile = localStorage.getItem('fh-active-profile');
    if (storedProfile && config.headers) {
      try {
        const profile = JSON.parse(storedProfile);
        if (profile?.id) {
          config.headers['X-Profile-Id'] = profile.id;
        }
      } catch { /* ignore */ }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { refreshToken, setTokens, logout } = useAuthStore.getState();
        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        const response = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

        setTokens(newAccessToken, newRefreshToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        // Re-schedule proactive refresh with the new token
        scheduleProactiveRefresh();

        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
